import { components } from '@feedfm/clips-services-api';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { CollectionCard } from '../../../components/CollectionCard';
import { useStudioAuthenticatedContext } from '../../../context/StudioContext';
import { StudioPage } from '../../../page-templates/StudioPage';
import {
  CollectionSortBy,
  sortCollectionsResponseRows,
} from '../../../util/collectionSort';
import * as styles from './Collections.styles';
import { HeaderSection } from './sections/HeaderSection/HeaderSection';

type CollectionSummaryResponseSingle =
  components['schemas']['CollectionsResponse']['collections'][number];

export const Collections = () => {
  const { backendClient } = useStudioAuthenticatedContext();
  const [summaries, setSummaries] =
    useState<null | Array<CollectionSummaryResponseSingle>>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<CollectionSortBy>(
    CollectionSortBy.DEFAULT,
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setSummaries(null);

      try {
        const { collections } = await backendClient.collections();
        if (cancelled) {
          return;
        }

        setSummaries(collections);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : 'An error occurred while fetching collections',
          );
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [backendClient]);

  const sortedSummaries = useMemo(
    () =>
      summaries === null
        ? null
        : sortCollectionsResponseRows(summaries, sortBy),
    [summaries, sortBy],
  );

  const handleSortChange = (newSortBy: CollectionSortBy) => {
    setSortBy(newSortBy);
  };

  return (
    <StudioPage>
      <HeaderSection
        collections={(sortedSummaries || []).map((row) => ({
          collectionId: row.summary.collectionId,
        }))}
        sortBy={sortBy}
        onSortChange={handleSortChange}
      />
      <Box sx={styles.container}>
        {loading && (
          <Box sx={styles.loadingContainer}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Box sx={styles.errorContainer}>
            <Typography sx={styles.errorText}>{error}</Typography>
          </Box>
        )}
        {sortedSummaries && (
          <Grid container spacing={3}>
            {sortedSummaries.map((row) => (
              <Grid
                size={{ xs: 12, sm: 6, md: 4 }}
                key={row.summary.collectionId}
              >
                <CollectionCard
                  collection={row.summary}
                  artworkPreSignedUrls={row.artworkPreSignedUrls}
                  thumbnailUrl={row.thumbnailUrl}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </StudioPage>
  );
};
